// ─── Central Pricing Configuration for Arkatechs ─────────────────────────────
// All starting prices and service definitions are strictly centralized here.
// All values are in INR (₹) according to the simplified Arkatechs pricing model.

export interface ServiceItem {
  id: string;
  name: string;
  cardTitle: string;
  priceDisplay: string;
  basePrice: number;
  isMonthly?: boolean;
  hasQuantity?: boolean;
  note?: string;
}

export interface PricingConfig {
  currencySymbol: string;
  services: ServiceItem[];
  ugcVideoUnitPrice: number;
  ugcVideoQuantityOptions: number[];
  digitalMarketingMonthlyPrice: number;
}

export const PRICING_CONFIG: PricingConfig = {
  currencySymbol: '₹',
  ugcVideoUnitPrice: 2000,
  ugcVideoQuantityOptions: [1, 2, 3, 5, 10],
  digitalMarketingMonthlyPrice: 10000,
  services: [
    {
      id: 'website',
      name: 'Website',
      cardTitle: 'Website',
      priceDisplay: 'From ₹5,000',
      basePrice: 5000,
    },
    {
      id: 'android-app',
      name: 'Android App',
      cardTitle: 'Android App',
      priceDisplay: 'From ₹20,000',
      basePrice: 20000,
    },
    {
      id: 'android-ios-app',
      name: 'Android + iOS App',
      cardTitle: 'Android + iOS App',
      priceDisplay: 'From ₹30,000',
      basePrice: 30000,
    },
    {
      id: 'server-hosting',
      name: 'Server & Hosting Support',
      cardTitle: 'Server & Hosting',
      priceDisplay: 'From ₹10,000',
      basePrice: 10000,
    },
    {
      id: 'logo-design',
      name: 'Logo Design',
      cardTitle: 'Logo Design',
      priceDisplay: 'From ₹2,000',
      basePrice: 2000,
    },
    {
      id: 'seo',
      name: 'SEO',
      cardTitle: 'SEO',
      priceDisplay: 'From ₹20,000',
      basePrice: 20000,
    },
    {
      id: 'ugc-video',
      name: 'UGC Video',
      cardTitle: 'UGC Video',
      priceDisplay: '₹2,000 / 30 sec',
      basePrice: 2000,
      hasQuantity: true,
      note: '30-second UGC video · ₹2,000 each',
    },
    {
      id: 'cloud-solutions',
      name: 'Cloud Solutions',
      cardTitle: 'Cloud Solutions',
      priceDisplay: 'From ₹10,000',
      basePrice: 10000,
    },
    {
      id: 'digital-marketing',
      name: 'Digital Marketing',
      cardTitle: 'Digital Marketing',
      priceDisplay: '15 posts / month · From ₹10,000',
      basePrice: 10000,
      isMonthly: true,
      note: 'Social Media Handling · 15 posts per month · ₹10,000 / month',
    },
  ],
};

export interface EstimatorSelections {
  selectedServices: string[];
  ugcVideoCount: number;
}

export interface ServiceBreakdownItem {
  id: string;
  name: string;
  displayPrice: string;
  amount: number;
  isMonthly?: boolean;
}

export interface EstimateResult {
  totalStartingPrice: number;
  formattedTotal: string;
  hasMonthly: boolean;
  selectedCount: number;
  breakdown: ServiceBreakdownItem[];
  title: string;
}

/**
 * Calculates the sum of selected base / starting prices.
 * Does NOT generate a quotation or false exact project price.
 */
export function calculateEstimate(selections: EstimatorSelections): EstimateResult {
  const { selectedServices, ugcVideoCount } = selections;
  let totalStartingPrice = 0;
  let hasMonthly = false;
  const breakdown: ServiceBreakdownItem[] = [];

  const serviceMap = new Map<string, ServiceItem>();
  PRICING_CONFIG.services.forEach((s) => serviceMap.set(s.id, s));

  selectedServices.forEach((serviceId) => {
    const service = serviceMap.get(serviceId);
    if (!service) return;

    if (serviceId === 'ugc-video') {
      const count = ugcVideoCount > 0 ? ugcVideoCount : 1;
      const countLabel = count >= 10 ? '10+' : `${count}`;
      const amount = count * PRICING_CONFIG.ugcVideoUnitPrice;
      totalStartingPrice += amount;
      breakdown.push({
        id: service.id,
        name: `UGC Video (${countLabel} × 30-sec video${count > 1 ? 's' : ''})`,
        displayPrice: `₹${amount.toLocaleString('en-IN')}+`,
        amount,
        isMonthly: false,
      });
    } else if (serviceId === 'digital-marketing') {
      hasMonthly = true;
      const amount = PRICING_CONFIG.digitalMarketingMonthlyPrice;
      totalStartingPrice += amount;
      breakdown.push({
        id: service.id,
        name: 'Digital Marketing (15 posts/mo)',
        displayPrice: `₹${amount.toLocaleString('en-IN')}+/mo`,
        amount,
        isMonthly: true,
      });
    } else {
      totalStartingPrice += service.basePrice;
      breakdown.push({
        id: service.id,
        name: service.name,
        displayPrice: `₹${service.basePrice.toLocaleString('en-IN')}+`,
        amount: service.basePrice,
        isMonthly: false,
      });
    }
  });

  const selectedCount = breakdown.length;
  const formattedTotal = totalStartingPrice > 0 ? `₹${totalStartingPrice.toLocaleString('en-IN')}+` : '₹0';
  const title = selectedCount > 1 ? 'ESTIMATED STARTING INVESTMENT' : 'YOUR STARTING ESTIMATE';

  return {
    totalStartingPrice,
    formattedTotal,
    hasMonthly,
    selectedCount,
    breakdown,
    title,
  };
}

export function formatCurrencyFull(amount: number): string {
  return `₹${amount.toLocaleString('en-IN')}`;
}
